import { useEffect, useState } from "react";
import { Alert, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import { Grid } from "../../leap-components/Grid";
import LeapSearch from "./Search";
import { CreateStaticSelectColumnFilter } from "../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  IAuthorisationTypesList,
  ILicenceSearchList,
  GetLicenceAdvancedSearchFetchUrl,
  ILicenceSearchRow,
  ISectorsList,
  ILicenceStatusesList,
  YesNoAllFilterValues,
} from "../../api/ILicenceAdvancedSearch";
import { formatDate } from "../../api/DateUtils";
import { GetLicenceVersionUrl } from "../../external-apps/Config";
import { InformationIcon } from "../../leap-components/icons/LeapIcons";

interface ISearchResultRow {
  licence_name: string;
  profile_number: string;
  organisation: string;
  active_application: string;
  county: string;
}

interface IAdvancedSearchProps {
  authorisationTypes: IAuthorisationTypesList;
  sectorsList: ISectorsList;
  statusesList: ILicenceStatusesList;
}

const GetLicenceVersionUrlFromRow = (row: any) => {
  return GetLicenceVersionUrl(
    row.original.authorisationtype,
    row.original.authorisationnumber.split("-")[0],
    row.original.licenceid,
    row.original.authorisationnumber
  );
};

const alertColor = "secondary";

const LeapAlert = () => (
  <Alert variant={alertColor} dismissible>
    <div className="alert-icon">
      <InformationIcon />
    </div>
    <div className="alert-message">
      <p>
        This is a global search function that enables users to search across EPA
        licence applications and authorisation records. It provides access to
        summary-level information for each authorisation. Use this search to
        locate both applications and authorisations across all licensing
        regimes, and apply filters such as authorisation status, sector, county,
        and more to refine your results.
      </p>
      <p>
        <b>Important:</b>When using multiple filters or adjusting filters,
        please refresh your browser and start the search again to ensure results
        are refined correctly.
      </p>
    </div>
  </Alert>
);

const AdvancedSearch = ({
  authorisationTypes,
  sectorsList,
  statusesList,
}: IAdvancedSearchProps) => {
  const [searchValue, setSearchValue] = useState("");

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const tableColumnsSmall: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "County",
      accessor: "county",
      Filter: false,
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
  ];

  const tableColumnsMedium: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: false,
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
    },
  ];

  const tableColumnsBig: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: false,
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Open for Submissions?",
      accessor: "openforsubmissions",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Open for Objections?",
      accessor: "openforobjections",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Priority Application this Year?",
      accessor: "pdduethisyear",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
    },
  ];

  let fetchUrlFn = (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any
  ) => {
    return GetLicenceAdvancedSearchFetchUrl(
      pageNo,
      recordsPerPage,
      searchText,
      filters
    );
  };

  let formatResponseFunction = (searchResponse: ILicenceSearchList) => {
    const array = searchResponse.list.map((result) => {
      return {
        licenceid: result.licenceid,
        authorisationnumber: result.authorisationnumber,
        authorisationname: result.authorisationname,
        authorisationtype: result.authorisationtype,
        organisation: result.organisation,
        finalauthorisationissueddate: formatDate(
          result.finalauthorisationissueddate
        ),
        county: result.county,
        status: result.status,
        openforsubmissions: result.openforsubmissions,
        openforobjections: result.openforobjections,
        pdduethisyear: result.pdduethisyear,
        sector: result.sector,
      } as ILicenceSearchRow;
    });

    return {
      list: array,
      length: searchResponse.length,
      page: searchResponse.page,
      count: searchResponse.count,
    };
  };

  const results = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
        />
      </div>
      <div className="d-none d-lg-none d-md-block">
        <Grid
          columns={tableColumnsMedium}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
        />
      </div>
      <div className="d-none d-lg-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
        />
      </div>
    </div>
  );

  const searchContent = (
    <div>
      <LeapAlert />
      <LeapSearch searchChangedFn={searchChangedFn} />
      {results}
    </div>
  );

  return searchContent;
};

export default AdvancedSearch;
